import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from './useAuth';
import { useAuthRedirect } from './useAuthRedirect';
import { useDriverData } from './useDriverData';
import type { LoginCredentials } from '@/types/auth';

export const useLoginForm = () => {
  const { t } = useTranslation();
  const [credentials, setCredentials] = useState<LoginCredentials>({
    username: '',
    password: '',
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { authenticate } = useAuth();
  const { getDriverData, updateSettingState } = useDriverData();
  const { redirectAfterLogin, navigateToPath } = useAuthRedirect();

  const handleInputChange = useCallback(
    (field: keyof LoginCredentials, value: string) => {
      setCredentials((prev) => ({
        ...prev,
        [field]: value,
      }));
      // 入力時にエラーをクリア
      if (error) {
        setError(null);
      }
    },
    [error],
  );

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();

      if (!credentials.username || !credentials.password) {
        setError('IDとPasswordを入力してください');

        return;
      }

      setIsLoading(true);
      setError(null);

      try {
        // 1. 認証処理
        const authResult = await authenticate(credentials);
        if (!authResult.success || !authResult.userInfo) {
          setError(authResult.error || 'ログインに失敗しました');

          return;
        }

        // 2. ドライバーデータ取得
        const driverDataResult = await getDriverData(authResult.userInfo.userId);
        if (!driverDataResult.success || !driverDataResult.driverData) {
          setError(driverDataResult.error || 'ドライバー情報の取得に失敗しました');

          return;
        }

        // 3. 設定状態を更新
        updateSettingState(authResult.userInfo, driverDataResult.driverData);

        // 4. リダイレクト処理
        const redirectResult = redirectAfterLogin(driverDataResult.driverData);
        if (redirectResult.success && redirectResult.redirectPath) {
          navigateToPath(redirectResult.redirectPath);
        } else {
          setError(redirectResult.error || 'リダイレクトに失敗しました');
        }
      } catch (err: unknown) {
        const errorMessage = err instanceof Error ? err.message : t('login.unexpectedError');
        setError(errorMessage);
      } finally {
        setIsLoading(false);
      }
    },
    [credentials, authenticate, getDriverData, updateSettingState, redirectAfterLogin, navigateToPath, t],
  );

  const resetForm = useCallback(() => {
    setCredentials({ username: '', password: '' });
    setError(null);
    setIsLoading(false);
  }, []);

  return {
    credentials,
    isLoading,
    error,
    handleInputChange,
    handleSubmit,
    resetForm,
  };
};
